import { useEffect, useState } from 'react';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useGetAlertAcknowledgements, useGetAlerts } from '@/domain/alerts';
import { useCreateBioscope, useGetActiveBioscopes } from '@/domain/bioscopes';
import { useGetClients } from '@/domain/client';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { getFromStorage, setToStorage } from '@/utils/localStorage';

import BioscopeListView from './BioscopeListView';
import {
    COLUMN_VISIBILITY_STORAGE_KEY,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_STORAGE_KEY,
    resolveColumnVisibility,
} from './bioscopeListColumns';

export default function BioscopeListContainer() {
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');
    const alerts = useGetAlerts();
    const activeBioscopes = useGetActiveBioscopes();
    // Calculate the time for two hours ago
    const twoHoursAgo = new Date().getTime() - 2 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const activeBioscopesList = activeBioscopes.data ?? [];
    const activeBioscopesInTimeRange = activeBioscopesList.filter((item) => {
        const lastAliveTime = new Date(item.lastAlive).getTime();
        // Return true if the lastAlive time is between two hours ago and the current time
        return lastAliveTime >= twoHoursAgo && lastAliveTime <= currentTime;
    });

    // Count bioscopes with online status from bioscopeStatus column
    const onlineStatusCount =
        activeBioscopes.data?.filter((item) => item.bioscopeStatus === 'online')?.length || 0;

    const acknowledgements = useGetAlertAcknowledgements();

    const clientList = useGetClients();
    const createBioscope = useCreateBioscope({
        onSettled: () => activeBioscopes.refetch(),
    });
    // Adding a bioscope is the add dialog's business: its progress and its failure show there,
    // and the list stays on the page.
    const loading = alerts.isLoading || activeBioscopes.isLoading || clientList.isLoading;
    const error = alerts.error || activeBioscopes.error || clientList.error;
    const [addBioscopeModalIsOpen, setAddBioscopeModalIsOpen] = useState(false);
    const [filter, setFilter] = useState('');

    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => {
        return resolveColumnVisibility(
            getFromStorage<ColumnVisibilityState>(COLUMN_VISIBILITY_STORAGE_KEY, {})
        );
    });

    useEffect(() => {
        setToStorage(COLUMN_VISIBILITY_STORAGE_KEY, columnVisibility);
    }, [columnVisibility]);

    const [pageSize, setPageSize] = useState<number>(() => {
        return getFromStorage(PAGE_SIZE_STORAGE_KEY, DEFAULT_PAGE_SIZE);
    });

    useEffect(() => {
        setToStorage(PAGE_SIZE_STORAGE_KEY, pageSize);
    }, [pageSize]);

    const onCreateBioscope = async (clientId: string, bioscopeId: string): Promise<void> => {
        await createBioscope.mutateAsync({ clientId, bioscopeId });
    };

    return (
        <BioscopeListView
            hasError={Boolean(error)}
            retry={retryFailedQueries(alerts, activeBioscopes, clientList)}
            loading={loading}
            activeCount={activeBioscopesInTimeRange?.length}
            onlineCount={onlineStatusCount}
            bioscopes={activeBioscopes.data ?? []}
            alerts={alerts.data ?? []}
            acknowledgements={acknowledgements.data ?? []}
            clients={clientList.data ?? []}
            filter={filter}
            onFilterChange={setFilter}
            columnVisibility={columnVisibility}
            // DataTable calls this with the full ColumnVisibilityState object
            onColumnVisibilityChange={setColumnVisibility}
            onColumnToggle={(columnId, visible) => {
                setColumnVisibility((prev) => ({
                    ...prev,
                    [columnId]: visible,
                }));
            }}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onBioscopeUpdate={() => activeBioscopes.refetch()}
            addBioscopeModalIsOpen={addBioscopeModalIsOpen}
            onAddBioscopeOpen={() => setAddBioscopeModalIsOpen(true)}
            onAddBioscopeClose={() => {
                setAddBioscopeModalIsOpen(false);
            }}
            createBioscope={onCreateBioscope}
            fishGroupsEnabled={fishGroupsEnabled}
        />
    );
}
